import { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TravelerCaptureSource } from '@bandido/shared/types';
import { useCaptureObjectUrl, useCaptureSourceDetail } from './source-media';
import { CapturePdfPreview } from './CapturePdfPreview';

const SourceThumbnailContent = ({ caseId, source }: { caseId: number; source: TravelerCaptureSource }) => {
  const { t } = useTranslation();
  const detail = useCaptureSourceDetail(caseId, source);
  const url = useCaptureObjectUrl(detail.data?.blob);
  const [failed, setFailed] = useState(false);
  if (detail.isError || failed) return <Typography variant="caption">{t('copilot.capture.previewError')}</Typography>;
  if (!detail.data?.blob || !url) return <CircularProgress size={20} />;
  if (source.mimetype === 'application/pdf') return <CapturePdfPreview blob={detail.data.blob} label={source.label} thumbnail />;
  return (
    <Box
      component="img"
      src={url}
      alt={source.label}
      onError={() => setFailed(true)}
      sx={{ maxWidth: '100%', maxHeight: 112, objectFit: 'contain' }}
    />
  );
};

export const CaptureSourceThumbnail = ({
  caseId,
  source,
  onOpen,
  compact = false,
}: {
  caseId: number;
  source: TravelerCaptureSource;
  onOpen: () => void;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: '150px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <ButtonBase
      ref={ref}
      onClick={onOpen}
      aria-label={`${t('copilot.capture.viewSource')}: ${source.label}`}
      sx={{
        width: compact ? 64 : { xs: 88, sm: 120 },
        minHeight: compact ? 60 : 104,
        maxHeight: compact ? 60 : 132,
        overflow: 'hidden',
        p: 1,
        flexShrink: 0,
        alignSelf: 'flex-start',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      {visible ? (
        <Box
          sx={
            compact
              ? {
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 104,
                height: 112,
                transform: 'translate(-50%, -50%) scale(0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
              : {}
          }
        >
          <SourceThumbnailContent caseId={caseId} source={source} />
        </Box>
      ) : (
        <Typography variant="caption">
          {source.mimetype === 'application/pdf' ? 'PDF' : t('copilot.capture.kind.file')}
        </Typography>
      )}
    </ButtonBase>
  );
};
